import React, { useEffect, useState, useCallback } from 'react';
import { reportsAPI } from '../api';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Download, TrendingUp, Users, DollarSign, Target } from 'lucide-react';
import { toast } from 'sonner';

const CHART_TOOLTIP_STYLE = {
  backgroundColor: 'white',
  border: '1px solid #E2E8F0',
  borderRadius: '8px'
};
const BAR_RADIUS_TOP = [8, 8, 0, 0];
const BAR_RADIUS_RIGHT = [0, 8, 8, 0];
const COLORS = ['#F59E0B', '#2563EB', '#22C55E', '#EF4444', '#F97316', '#8B5CF6', '#06B6D4', '#EC4899'];

export const ReportsPage = () => {
  const [report, setReport] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      // Single scope: every tile and chart on this page comes from /reports/leads.
      const reportRes = await reportsAPI.getLeadsReport(params);
      setReport(reportRes.data);
    } catch (error) {
      // Silent
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo) params.date_to = new Date(dateTo).toISOString();
      const response = await reportsAPI.exportCSV(params);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_report_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
    </div>;
  }

  const serviceChart = (report?.service_wise || []).map(s => ({ name: s.name, value: s.count }));
  const sourceChart = (report?.source_wise || []).map(s => ({ name: s.name, value: s.count }));
  const employeeChart = (report?.employee_wise || []).map(s => ({ name: s.name, value: s.count }));
  const monthlyTrend = report?.monthly_trend || [];
  const wonLost = report?.won_vs_lost || { won: 0, lost: 0, open: 0 };

  const wonLostData = [
    { name: 'Won', value: wonLost.won, color: '#22C55E' },
    { name: 'Lost', value: wonLost.lost, color: '#EF4444' },
    { name: 'Open', value: wonLost.open, color: '#3B82F6' }
  ];

  return (
    <div className="space-y-6" data-testid="reports-container">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-outfit font-semibold">Lead Report</h1>
          <p className="text-muted-foreground mt-2">Service-wise, source-wise, and employee-wise insights</p>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={exporting}
          data-testid="reports-export-csv"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {/* Date Filter */}
      <div className="clay-card p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium mb-1">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" data-testid="reports-date-from" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-sm" data-testid="reports-date-to" />
        </div>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }} className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg">
            Reset
          </button>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="clay-card p-6">
          <Users className="h-8 w-8 text-blue-500 mb-3" />
          <p className="text-sm text-muted-foreground">Total Leads</p>
          <p className="text-3xl font-outfit font-semibold">{report?.total_leads || 0}</p>
        </div>
        <div className="clay-card p-6">
          <Target className="h-8 w-8 text-orange-500 mb-3" />
          <p className="text-sm text-muted-foreground">Conversion Rate</p>
          <p className="text-3xl font-outfit font-semibold">{report?.conversion_rate || 0}%</p>
        </div>
        <div className="clay-card p-6">
          <TrendingUp className="h-8 w-8 text-green-500 mb-3" />
          <p className="text-sm text-muted-foreground">Won Leads</p>
          <p className="text-3xl font-outfit font-semibold">{wonLost.won}</p>
        </div>
        <div className="clay-card p-6">
          <DollarSign className="h-8 w-8 text-primary mb-3" />
          <p className="text-sm text-muted-foreground">Lost Leads</p>
          <p className="text-3xl font-outfit font-semibold">{wonLost.lost}</p>
        </div>
      </div>

      {/* Service-wise Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="clay-card p-6" data-testid="reports-service-wise">
          <h3 className="text-lg font-outfit font-medium mb-4">Service-wise Analytics</h3>
          {serviceChart.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground">No service data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={serviceChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" />
                <YAxis stroke="#64748B" />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                <Bar dataKey="value" fill="#F59E0B" radius={BAR_RADIUS_TOP} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="clay-card p-6" data-testid="reports-won-lost">
          <h3 className="text-lg font-outfit font-medium mb-4">Won vs Lost vs Open</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={wonLostData} cx="50%" cy="50%" labelLine={false}
                label={(entry) => `${entry.name}: ${entry.value}`} outerRadius={100} dataKey="value">
                {wonLostData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Trend */}
      {monthlyTrend.length > 0 && (
        <div className="clay-card p-6">
          <h3 className="text-lg font-outfit font-medium mb-4">Monthly Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={monthlyTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" stroke="#64748B" />
              <YAxis stroke="#64748B" />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Legend />
              <Area type="monotone" dataKey="leads" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.3} />
              <Area type="monotone" dataKey="won" stroke="#22C55E" fill="#22C55E" fillOpacity={0.3} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Source & Employee */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="clay-card p-6" data-testid="reports-source-wise">
          <h3 className="text-lg font-outfit font-medium mb-4">Source-wise</h3>
          {sourceChart.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sourceChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" stroke="#64748B" angle={-45} textAnchor="end" height={100} fontSize={11} />
                <YAxis stroke="#64748B" />
                <Tooltip />
                <Bar dataKey="value" fill="#2563EB" radius={BAR_RADIUS_TOP} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="clay-card p-6" data-testid="reports-employee-wise">
          <h3 className="text-lg font-outfit font-medium mb-4">Employee-wise</h3>
          {employeeChart.length === 0 ? (
            <div className="flex items-center justify-center h-[300px] text-sm text-muted-foreground">No data</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={employeeChart} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" stroke="#64748B" />
                <YAxis type="category" dataKey="name" stroke="#64748B" width={150} />
                <Tooltip />
                <Bar dataKey="value" fill="#22C55E" radius={BAR_RADIUS_RIGHT} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};
